import { ExpressionError, type BinaryOp, type Node } from './ast';
import { tokenize, type Token } from './tokenize';

interface Precedence {
  readonly level: number;
  readonly rightAssociative: boolean;
}

const BINARY: Readonly<Record<BinaryOp, Precedence>> = {
  '+': { level: 1, rightAssociative: false },
  '-': { level: 1, rightAssociative: false },
  '*': { level: 2, rightAssociative: false },
  '/': { level: 2, rightAssociative: false },
  '%': { level: 2, rightAssociative: false },
  '^': { level: 3, rightAssociative: true },
};

export type ParseResult =
  | { readonly ok: true; readonly node: Node }
  | { readonly ok: false; readonly error: ExpressionError };

export function parse(source: string): ParseResult {
  try {
    return { ok: true, node: parseOrThrow(source) };
  } catch (error) {
    if (error instanceof ExpressionError) return { ok: false, error };
    throw error;
  }
}

export function parseOrThrow(source: string): Node {
  const parser = new Parser(tokenize(source));
  const node = parser.parseExpression(0);
  parser.expectEnd();
  return node;
}

class Parser {
  private index = 0;

  constructor(private readonly tokens: Token[]) {}

  parseExpression(minLevel: number): Node {
    let left = this.parseUnary();

    for (;;) {
      const token = this.peek();
      if (token.type !== 'operator') break;
      const op = token.text as BinaryOp;
      const precedence = BINARY[op];
      if (!precedence || precedence.level < minLevel) break;
      this.next();
      const nextLevel = precedence.rightAssociative ? precedence.level : precedence.level + 1;
      left = { kind: 'binary', op, left, right: this.parseExpression(nextLevel) };
    }

    return left;
  }

  private parseUnary(): Node {
    const token = this.peek();
    if (token.type === 'operator' && (token.text === '-' || token.text === '+')) {
      this.next();
      return { kind: 'unary', op: token.text, operand: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const token = this.next();

    if (token.type === 'number') {
      return { kind: 'number', value: Number(token.text) };
    }

    if (token.type === 'variable') {
      return { kind: 'variable', name: token.text.slice(1) };
    }

    if (token.type === 'identifier') {
      if (this.peek().type !== 'lparen') return { kind: 'identifier', name: token.text };
      this.next();
      const args: Node[] = [];
      if (this.peek().type !== 'rparen') {
        args.push(this.parseExpression(0));
        while (this.peek().type === 'comma') {
          this.next();
          args.push(this.parseExpression(0));
        }
      }
      this.expect('rparen', ')');
      return { kind: 'call', name: token.text, args };
    }

    if (token.type === 'lparen') {
      const node = this.parseExpression(0);
      this.expect('rparen', ')');
      return node;
    }

    throw new ExpressionError(
      token.type === 'end' ? 'Unexpected end of expression' : `Unexpected "${token.text}"`,
      token.position,
    );
  }

  expectEnd(): void {
    const token = this.peek();
    if (token.type !== 'end') {
      throw new ExpressionError(`Unexpected "${token.text}"`, token.position);
    }
  }

  private expect(type: Token['type'], text: string): void {
    const token = this.next();
    if (token.type !== type) {
      throw new ExpressionError(`Expected "${text}"`, token.position);
    }
  }

  private peek(): Token {
    return this.tokens[this.index] as Token;
  }

  private next(): Token {
    const token = this.peek();
    if (token.type !== 'end') this.index += 1;
    return token;
  }
}
